import React, { useState, useEffect, useCallback, useRef } from 'react';
import styled, { keyframes } from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBell } from '@fortawesome/free-solid-svg-icons';
import moment from 'moment';
import Modal from '../Modal';
import { StyledModalContainer } from '../Styled';

const pulse = keyframes`
  0% { transform: scale(1); opacity: 1; }
  50% { transform: scale(1.4); opacity: 0.6; }
  100% { transform: scale(1); opacity: 1; }
`;

const StyledNotificationsSectionContainer = styled.section`
  width: 55px;
  height: 100%;
  border-left: 1px solid #e5e5e5;
  position: relative;
  @media (max-width: 600px) {
    position: static;
  }
`;

const StyledNotificationsIconContainer = styled.section`
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;

  button {
    width: 100%;
    height: 100%;
    border: none;
    background: transparent;
    cursor: pointer;
    display: flex;
    justify-content: center;
    align-items: center;
    position: relative;
    transition: all 0.3s ease;

    svg {
      font-size: 20px;
      color: #707070;
      transition: color 0.3s ease;
    }

    &:hover,
    &.active {
      background-color: ${({ theme }) => theme.colors.secondaryColor || '#0078ce'};
      border-radius: 8px;

      svg {
        color: #fff !important;
      }
    }
  }
`;

const StyledNotificationsContent = styled.section`
  position: absolute;
  width: 380px;
  max-height: 400px;
  background-color: #fff;
  top: 55px;
  right: 0;
  border-radius: 8px;
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.15);
  overflow: hidden;
  z-index: 10;

  @media (max-width: 600px) {
    width: 100%;
    right: 0;
  }
`;

const StyledNotificationsContentHeader = styled.section`
  background-color: ${({ theme }) => theme.colors.secondaryColor || '#0078ce'};
  h3 {
    margin: 0;
    padding: 12px 16px;
    font-size: 14px;
    color: #fff;
  }
`;

const StyledNotificationsContentLinks = styled.section`
  max-height: 300px;
  overflow-y: auto;
  ul {
    list-style: none;
    margin: 0;
    padding: 0;

    li {
      border-bottom: 1px solid #e5e5e5;
      transition: background-color 0.3s;

      a {
        font-size: 14px;
        color: #000;
        display: flex;
        align-items: center;
        padding: 12px 16px;
        text-decoration: none;

        svg {
          font-size: 20px;
          color: ${({ theme }) => theme.colors.primaryColor || '#0095ff'};
          margin-right: 12px;
        }
      }

      &:hover {
        background-color: #f5f5f5;
      }
    }
  }
`;

const StyledRedDot = styled.section`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: red;
  position: absolute;
  top: 20%;
  right: 20%;
  animation: ${pulse} 1.2s infinite;
`;

const NoNotifications = styled.section`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 20px;
  p {
    margin: 0;
    font-size: 14px;
    color: #777;
  }
`;

const NotificationItem = styled.section`
  cursor: pointer;
  &:hover {
    background-color: #f8f8f8;
  }
`;

const NotificationItemHeading = styled.section`
  background-color: #f0f0f0;
  padding: 8px 16px;
  h3 {
    margin: 0;
    font-size: 14px;
    font-weight: 500;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

const NotificationItemBody = styled.section`
  padding: 4px 16px 8px;
  font-size: 13px;
  color: #555;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const NotificationItemFooter = styled.section`
  display: flex;
  justify-content: flex-end;
  padding: 0 16px 8px;
  span {
    font-size: 11px;
    color: #999;
    font-style: italic;
  }
`;

const ModalTitle = styled.h4`
  font-size: 16px;
  font-weight: bold;
  margin: 10px 0;
`;

const ModalMessage = styled.p`
  font-size: 14px;
  color: #555;
  line-height: 1.5;
  margin: 0 0 20px;
`;

const NotificationsSection = ({ notifications, updateNotification }) => {
  const [expandNotifications, setExpandNotifications] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [notificationData, setNotificationData] = useState(null);
  const notificationsRef = useRef(null);

  const onClickExpandNotifications = (e) => {
    e.preventDefault();
    setExpandNotifications((prev) => !prev);
  };

  const closeNotificationsList = useCallback((e) => {
    if (notificationsRef.current && !notificationsRef.current.contains(e.target)) {
      setExpandNotifications(false);
    }
  }, []);

  useEffect(() => {
    document.addEventListener('click', closeNotificationsList, true);
    return () => document.removeEventListener('click', closeNotificationsList, true);
  }, [closeNotificationsList]);

  const hasNewNotifications = notifications.some((n) => n.status === 'Sent');

  return (
    <>
      <StyledNotificationsSectionContainer ref={notificationsRef}>
        <StyledNotificationsIconContainer>
          <button
            type="button"
            className={expandNotifications ? 'active' : ''}
            onClick={onClickExpandNotifications}
          >
            <FontAwesomeIcon icon={faBell} />
            {hasNewNotifications && <StyledRedDot />}
          </button>
        </StyledNotificationsIconContainer>

        {expandNotifications && (
          <StyledNotificationsContent>
            <StyledNotificationsContentHeader>
              <h3>Notifications</h3>
            </StyledNotificationsContentHeader>
            <StyledNotificationsContentLinks>
              {notifications.length === 0 && (
                <NoNotifications>
                  <p>No notifications found</p>
                </NoNotifications>
              )}
              {notifications.map((item) => (
                <NotificationItem
                  key={item.id}
                  onClick={() => {
                    setExpandNotifications(false);
                    setNotificationData(item);
                    setShowModal(true);
                    updateNotification(item.id);
                  }}
                >
                  <NotificationItemHeading>
                    <h3>{item.title}</h3>
                  </NotificationItemHeading>
                  <NotificationItemBody>{item.message}</NotificationItemBody>
                  <NotificationItemFooter>
                    <span>{moment(item.createdOn).format('DD-MM-YYYY')}</span>
                  </NotificationItemFooter>
                </NotificationItem>
              ))}
            </StyledNotificationsContentLinks>
          </StyledNotificationsContent>
        )}
      </StyledNotificationsSectionContainer>

      <Modal
        modalStatus={showModal}
        setModalStatus={() => {
          setShowModal(false);
          setNotificationData(null);
        }}
        hideCloseButton
      >
        <StyledModalContainer width="500px">
          <h3>Notification</h3>
          <ModalTitle>{notificationData?.title}</ModalTitle>
          <ModalMessage>{notificationData?.message}</ModalMessage>
        </StyledModalContainer>
      </Modal>
    </>
  );
};

export default NotificationsSection;
